/**
 * The ONE column a progression surface is looking at, and everything that column implies.
 *
 * An exercise carries a MAP of `field → rule` (`progressionRules.ts`). A surface shows one of them
 * at a time; an AXIS is that choice made concrete: the measurement field the column holds, the
 * `SetDraft` number it reads, the bounds a typed value is clamped to, and how the displayed number
 * relates to the stored one. The %1RM axis is the one that names no field — a percent is set
 * CONSTRUCTION rather than a measurement — which is why `field` is nullable and why its grid
 * (`onPercentGrid`) is stated here rather than read from the dictionary.
 *
 * ── There is no hand-written list of axes any more, and that is the point ────────────────────
 * Four of them were declared here as constants while the document accepted a rule on any of nine
 * columns, so a rule authored on reps or duration had nowhere to be SEEN: no surface could offer the
 * column, the matrix could not render it, and the editor opened on the load instead — over a rule it
 * was not showing. An axis is now DERIVED from the dictionary for whichever column the exercise
 * measures, so the columns a surface can show and the columns a rule may key are one list.
 *
 * Neither the typed bounds nor the progression grid is restated here: both belong to the field
 * dictionary (weight snaps to a 1,25 kg plate while it is TYPED in 0,5), and that dictionary is a
 * byte copy of the backend's. Reading it rather than restating it is what keeps the two runtimes'
 * arithmetic identical — and what stops the matrix and the set table disagreeing about what a
 * trainer may type into the same cell.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionAxis.ts`, 1:1 ───────
 * What replaced the previous version of this file: `ProgressionMetric` and the three-member
 * `availableAxisMetrics` are gone with the wire enum they mirrored — the map's KEY is the axis now —
 * and `WEIGHT_AXIS`/`RPE_AXIS`/`RIR_AXIS` survive as named derivations rather than as literals.
 * `normaliseTypedValue` keeps its name (mobile calls it `clampTypedToAxis`) because three modules in
 * this app already spell it that way; its body is mobile's.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  clampMeasurementValue,
  isEffortField,
  measurementFieldDefinition,
  renderableColumns,
  type MeasurementFieldKey,
} from '~/shared/measurement';

import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { carriesProgression, type ProgressionRules } from './progressionRules';
import { measurementValueKey, type SetValueKey } from './setGridFields';

/**
 * What an axis IS, for the one place that has to tell them apart: a measurement column, or the %1RM
 * reading of the load column. `'percent'` is the only member that names no field.
 */
export type ProgressionAxisKind = MeasurementFieldKey | 'percent';

/**
 * A measurement column an axis can point at. Every field the dictionary holds is a candidate; which
 * ones an EXERCISE offers is `availableAxisFields`, which reads its definition.
 */
export type ProgressionAxisField = MeasurementFieldKey;

/**
 * The `SetDraft` numeric field an axis reads and writes as its base value — the set table's own map,
 * not a second copy of it. The two surfaces edit the same cell from two screens, so a column reading
 * `prescribedRounds` in the table and something else in the matrix is a defect with no way to notice.
 */
export type AxisValueField = SetValueKey;

/** A projection axis. */
export interface ProgressionAxis {
  readonly kind: ProgressionAxisKind;
  /** The measurement field this axis writes — the key its rule sits under. Null for the %1RM axis. */
  readonly field: MeasurementFieldKey | null;
  readonly setField: AxisValueField;
  /** Inclusive bounds of a value the trainer may TYPE (raw stored units — percent is the fraction). */
  readonly min: number;
  readonly max: number;
  /** Raw → displayed. 100 for percent (0.75 reads "75"), 1 everywhere else. */
  readonly displayScale: number;
}

/**
 * The axis one measurement column reads as. Bounds come from the DICTIONARY rather than being
 * restated here: they are the same bounds the set table clamps a typed value to, and the matrix and
 * the table edit the same cell from two screens. Restating them was a second source of truth that
 * had already drifted — the weight axis accepted 9999,99 kg where the dictionary (and the backend
 * column) stop at 2000, and the RPE axis refused a typed 0 the table two clicks away accepts.
 */
function fieldAxis(field: MeasurementFieldKey, setField: AxisValueField): ProgressionAxis {
  const { min, max } = measurementFieldDefinition(field);
  return {
    kind: field,
    field,
    setField,
    min: min ?? 0,
    max: max ?? Number.POSITIVE_INFINITY,
    displayScale: 1,
  };
}

/** kg — the load column in absolute mode, and the axis every fallback lands on. */
export const WEIGHT_AXIS: ProgressionAxis = fieldAxis('weight', 'prescribedWeight');

/**
 * The grain the fraction column is STORED at — `numeric(5,4)` on the backend. A fraction landing
 * finer than this would be rounded by the database rather than by the engine, and the two runtimes
 * would then disagree about what was saved (`ProgressionCalculator.PercentStep`).
 */
export const PERCENT_STEP = 0.0001;

/**
 * The smallest fraction the column can hold. A descent stops here rather than at zero: a set priced
 * at none of the client's max is the ABSENCE of a prescription, not a lighter one.
 */
export const PERCENT_FLOOR = 0.0001;

/**
 * The whole of the client's max, and the top of the axis — the number the SAVE reads back (the
 * wire's percent range is `(0, 1]`). A row materialised above it could never be saved again, so a
 * climb plateaus here. The exercise's MULTIPLIER is the way to programme above a tested max: it is a
 * factor rather than a fraction and keeps its own wider range.
 */
export const PERCENT_CEILING = 1;

/**
 * %1RM as a 0–1 fraction; displayed ×100. The one axis the dictionary cannot supply, because a
 * percent is set CONSTRUCTION rather than a measurement — which is the same reason its `field` is
 * null, and why its grid is named here instead of read from a field definition.
 */
export const PERCENT_AXIS: ProgressionAxis = {
  kind: 'percent',
  field: null,
  setField: 'prescribedPercent',
  min: 0,
  max: PERCENT_CEILING,
  displayScale: 100,
};

/** RPE — named because several surfaces and their tests reach for it; derived like every other. */
export const RPE_AXIS: ProgressionAxis = fieldAxis('rpe', 'prescribedRpe');

/** RIR — see `RPE_AXIS`. */
export const RIR_AXIS: ProgressionAxis = fieldAxis('rir', 'prescribedRir');

/**
 * True for an EFFORT axis (RPE / RIR) — the columns carrying the client's report of how a set felt
 * rather than work they were set. Read off the dictionary's value type, never a field list, so it
 * stays right when the dictionary gains another effort scale. The %1RM axis is never one.
 */
export function isEffortAxis(axis: ProgressionAxis): boolean {
  return axis.field !== null && isEffortField(axis.field);
}

/**
 * Resolve the axis for one column plus the weight-prescription mode — the ONLY mapping point.
 *
 * `weight` follows the prescription mode (`PercentOfOneRm` → the percent axis, otherwise kg); every
 * other column is itself, whatever the load is priced in. Null for a column the wire holds as TEXT
 * (tempo): it has no numeric cell to read, so it has no arithmetic and can never be an axis.
 * `availableAxisFields` never offers one, so the null states what that filter already guarantees
 * rather than a case any caller reaches.
 */
export function axisForField(
  field: ProgressionAxisField,
  prescriptionMode: string,
): ProgressionAxis | null {
  if (field === 'weight') return prescriptionMode === 'PercentOfOneRm' ? PERCENT_AXIS : WEIGHT_AXIS;
  const setField = measurementValueKey(field);
  return setField === null ? null : fieldAxis(field, setField);
}

/**
 * The columns an exercise's DEFINITION lets a surface look at, in the dictionary's canonical order.
 *
 * Two filters, answering different questions. The exercise's own contract says which columns exist
 * for it (exercise-measurements R9 — a rule gated on a column it does not measure is refused at
 * save, and a rule that WRITES one "resolves nothing and degrades silently"). `renderableColumns`
 * says which of those the app can put a numeric cell under, which today withholds speed and tempo.
 *
 * REST is deliberately absent even though the set table renders a column for it: it is not a
 * measurement field — the backend enum has no such member — so no rule can be keyed on it.
 */
export function availableAxisFields(
  measurementFields: readonly MeasurementFieldKey[],
): ProgressionAxisField[] {
  return renderableColumns(measurementFields);
}

/**
 * The axis field an exercise OPENS on: among the columns its DEFINITION measures, the first carrying
 * a real progression, falling back to the first it measures at all.
 *
 * The measurement contract comes FIRST and the rules only choose within it. The other order —
 * "wherever a rule happens to sit" — puts a surface on a column the movement does not have: the
 * backend normalises an unauthored document to a flat `Static` on WEIGHT, so a push-up comes back
 * from every save carrying a weight key. A rule on a column the exercise does not measure has no
 * effect the trainer could ever see, so it may not decide what they are shown.
 *
 * `carriesProgression` is the second half: a flat rule is not an authored one, so the same
 * normalisation artefact cannot outrank a real rule on a column the movement DOES measure either.
 *
 * The order is the DICTIONARY's (R4): reps, weight, duration, distance, rounds, then the two effort
 * scales. It is read from the one place every surface reads that order from, rather than stated here.
 */
export function openingAxisField(
  rules: ProgressionRules,
  measurementFields: readonly MeasurementFieldKey[],
): ProgressionAxisField {
  const measured = availableAxisFields(measurementFields);
  return measured.find((field) => carriesProgression(rules[field])) ?? measured[0] ?? 'weight';
}

/**
 * The axis an exercise opens on — `openingAxisField` resolved against its prescription mode. Total:
 * the opening column is drawn from `availableAxisFields`, which only ever offers columns that HAVE
 * an axis, and an exercise measuring none of them falls back to the load column.
 */
export function axisForExercise(
  exercise: Pick<ExerciseDraft, 'progressionRules' | 'measurementFields' | 'prescriptionMode'>,
): ProgressionAxis {
  return (
    axisForField(
      openingAxisField(exercise.progressionRules, exercise.measurementFields),
      exercise.prescriptionMode,
    ) ?? WEIGHT_AXIS
  );
}

/** Round to 2 decimals to shed float noise while keeping exact plate and half-point fractions. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The same, at the grain the fraction column is stored at — a snapped 0,8405 must not read back as
 * 0,8405000000000001, which is what multiplying by a float step leaves behind.
 */
function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** Clamp a TYPED value into the axis's inclusive bounds. */
export function clampToAxis(value: number, axis: ProgressionAxis): number {
  return Math.min(axis.max, Math.max(axis.min, value));
}

/**
 * A HAND-TYPED value as this axis stores it — the one gate every typed number on a progression
 * surface passes through, whether it lands on a set or on a per-cycle pin.
 *
 * `clampToAxis` bounds and nothing else, which is not enough for a measurement column: the
 * dictionary's whole-number rule is what keeps a typed "8,5" out of an `int?` wire column, and
 * System.Text.Json rejects a fraction there and 400s the ENTIRE plan save — on a screen the trainer
 * left minutes ago. Reading it off `clampMeasurementValue` is also what makes the matrix and the set
 * table agree about what a trainer may type into the same cell from two screens.
 *
 * The %1RM fraction has no dictionary column, so it keeps the axis's own bounds. That IS its gate,
 * not an exception to having one.
 *
 * Distinct from `snapToAxis`, and the two must not be merged: that one puts MACHINE-computed values
 * on the field's progression grid, which is a coarser thing than a trainer is allowed to type.
 */
export function normaliseTypedValue(value: number, axis: ProgressionAxis): number {
  return axis.field === null
    ? clampToAxis(value, axis)
    : clampMeasurementValue(axis.field, value);
}

/**
 * Round to the nearest multiple of `step`, ties AWAY FROM ZERO — the rule the backend's
 * `ProgressionCalculator` uses. JavaScript's `Math.round` breaks ties toward +∞ instead, so a
 * descending rule landing exactly on a half step (a −2,5 kg deload reaching −0,625) would round one
 * grid line apart from the server's answer.
 */
function roundAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * Put a MACHINE-computed value on a field's PROGRESSION grid: snap to the field's progression step,
 * then clamp into its progression range. Snap first, then bound — every declared bound sits on its
 * own step, so clamping last can never hand back a value off the grid. This is the client half of
 * the arithmetic `progression-projection-parity.json` pins.
 *
 * A field the dictionary gives no progression step (the text notation) has no grid to sit on and is
 * returned untouched; nothing can author a rule on it, and the backend's expander rides such a cell
 * through too.
 */
export function snapToProgressionGrid(value: number, field: MeasurementFieldKey): number {
  const { progressionStep, progressionMin, progressionMax } = measurementFieldDefinition(field);
  const snapped =
    progressionStep === null
      ? value
      : round2(roundAwayFromZero(value / progressionStep) * progressionStep);
  return Math.min(
    progressionMax ?? Number.POSITIVE_INFINITY,
    Math.max(progressionMin ?? Number.NEGATIVE_INFINITY, snapped),
  );
}

/**
 * Put a MACHINE-computed FRACTION of the client's max on its column's grid: snapped to the grain the
 * column is stored at, then held between the floor and the whole max. The mirror of the backend's
 * `ProgressionCalculator.OnPercentGrid`.
 *
 * The fraction is SAVED: a projection of 0,861509… is stored as 0,8615 by the column and a
 * projection above the whole max is refused outright, so a matrix without this grid previews cycles
 * the plan cannot hold.
 */
export function onPercentGrid(value: number): number {
  return Math.min(
    PERCENT_CEILING,
    Math.max(PERCENT_FLOOR, round4(roundAwayFromZero(value / PERCENT_STEP) * PERCENT_STEP)),
  );
}

/**
 * Snap a MACHINE-computed value on the active axis — the field's progression grid, or the fraction
 * column's own. Only machine-derived values pass through here; never the authored base, never a
 * hand-typed override.
 */
export function snapToAxis(value: number, axis: ProgressionAxis): number {
  return axis.field === null ? onPercentGrid(value) : snapToProgressionGrid(value, axis.field);
}

/** The raw base value a set contributes on the given axis — the axis's own per-set field. */
export function axisBaseOf(set: SetDraft, axis: ProgressionAxis): number | null {
  return set[axis.setField] ?? null;
}
